"use client";

import React from "react";
import { Calendar } from "lucide-react";
import { motion } from "framer-motion";
import { IoHappy } from "react-icons/io5";
import { MdTravelExplore } from "react-icons/md";
import { GoPackage } from "react-icons/go";

interface StatsProps {
  aboutUsStats?: {
    year_experience?: string;
    happy_travellers?: string;
    total_packages?: string;
    travel_history?: string;
  };
}

const Stats = ({ aboutUsStats }: StatsProps) => {
  const summaryData = [
    {
      icon: Calendar,
      number: aboutUsStats?.year_experience || "6",
      description: "Yrs Experience",
      size: "small",
      delay: 0,
      speed: "12s",
    },
    {
      icon: IoHappy,
      number: aboutUsStats?.happy_travellers || "100",
      description: "Happy Travellers",
      size: "large",
      delay: 0.1,
      speed: "18s",
    },
    {
      icon: GoPackage,
      number: aboutUsStats?.total_packages || "80",
      description: "Total Packages",
      size: "small",
      delay: 0.2,
      speed: "15s",
    },
    {
      icon: MdTravelExplore,
      number: aboutUsStats?.travel_history || "1K",
      description: "Travel History",
      size: "large",
      delay: 0.3,
      speed: "22s",
    },
  ];

  return (
    <section className="w-full py-24 bg-gray-50 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-16 lg:gap-12 items-center justify-items-center">
          {summaryData.map((item, index) => {
            const Icon = item.icon;
            const isLarge = item.size === "large";

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: item.delay }}
                className="group relative flex items-center justify-center cursor-default"
              >
                <div
                  className={`
                    ${isLarge ? "w-64 h-64 md:w-72 md:h-72" : "w-52 h-52 md:w-60 md:h-60"}
                    rounded-full border-2 border-tertiary-100 relative flex items-center justify-center 
                    transition-colors duration-500 group-hover:border-tertiary-500
                  `}
                >
                  <div
                    className={`
                      ${isLarge ? "w-56 h-56 md:w-64 md:h-64" : "w-44 h-44 md:w-52 md:h-52"}
                      rounded-full border border-gray-50
                      flex flex-col items-center justify-center cursor-pointer
                      transition-all duration-500 group-hover:shadow-2xl group-hover:shadow-tertiary-100
                    `}
                  >
                    <span className="text-5xl font-black text-primary-500 mb-1 tracking-tighter">
                      {item.number}
                    </span>
                    <span className="text-xs text-gray-400 font-bold uppercase tracking-[0.2em] text-center px-8">
                      {item.description}
                    </span>
                  </div>

                  <div
                    className="absolute -inset-[2px] rounded-full pointer-events-none animate-orbit-variable [animation-play-state:paused] group-hover:[animation-play-state:running]"
                    style={
                      { "--orbit-speed": item.speed } as React.CSSProperties
                    }
                  >
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2">
                      <div
                        className="bg-tertiary-500 rounded-full w-12 h-12 shadow-lg border-[3px] border-white flex items-center justify-center animate-counter-orbit-variable [animation-play-state:paused] group-hover:[animation-play-state:running]"
                        style={
                          { "--orbit-speed": item.speed } as React.CSSProperties
                        }
                      >
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      <style jsx global>{`
        @keyframes orbit {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        @keyframes counter-orbit {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(-360deg);
          }
        }
        .animate-orbit-variable {
          animation: orbit var(--orbit-speed, 10s) linear infinite;
        }
        .animate-counter-orbit-variable {
          animation: counter-orbit var(--orbit-speed, 10s) linear infinite;
        }
      `}</style>
    </section>
  );
};

export default Stats;
